// Logging helpers that use window.DEBUG if available
function log(...args) {
  if (window.DEBUG) console.log('🛒 RebuyUtils:', ...args);
}

function logError(...args) {
  if (window.DEBUG) console.error('🛒 RebuyUtils ERROR:', ...args);
}

log('File loading started');
window.RebuyUtils = window.RebuyUtils || {};
log('Object created');

window.RebuyUtils = {
  log: log,
  logError: logError,

  // Fetch the latest cart data from Rebuy
  fetchCart: async () => {
    try {
      log('Fetching cart data');
      const cart = await window.Rebuy.Cart.getCart();
      log('Cart fetched successfully', cart);
      return cart;
    } catch (error) {
      logError('Cart fetch error:', error);
      return null;
    }
  },

  // Find the discount code applied to the cart
  getActiveDiscount: () => {
    const discount = window.Rebuy?.SmartCart?.discount?.discount?.value || 0;
    log('Active discount:', discount);
    return discount;
  },

  // Calculate prices for an item, accounting for quantity and discount
  calculateItemPrices(cartItem, discountPercent = 0, itemTypeOrElement) {
    const quantity = cartItem.quantity || 1;

    // Determine item type using utility function if element is provided
    // Or use the directly passed item type string
    let itemType = typeof itemTypeOrElement === 'string' ? itemTypeOrElement : 'unknown';

    if (itemTypeOrElement && typeof itemTypeOrElement !== 'string') {
      // itemTypeOrElement is an element, use the utility function
      itemType = window.RebuyUtils.isSubscriptionItem(itemTypeOrElement);
    }

    // Determine if item is a subscription based on item type
    const isSubscription =
      itemType && (itemType === 'pure-subscription' || itemType === 'switched-to-subscription');

    // Base price in dollars (Rebuy stores in cents)
    const basePrice = parseFloat(cartItem.price) / 100;

    // Get variant info
    const variantId = cartItem.variant_id;
    const variantTitle = cartItem.variant_title || '';

    // Check if item has a compare-at price (sale item)
    const hasCompareAtPrice =
      cartItem.product?.selected_variant?.compare_at_price &&
      parseFloat(cartItem.product.selected_variant.compare_at_price) > basePrice;

    // Get compare-at price if available
    const compareAtPrice = hasCompareAtPrice
      ? parseFloat(cartItem.product.selected_variant.compare_at_price)
      : null;

    let finalPrice = basePrice;
    let shouldShowCompareAt = hasCompareAtPrice;
    let effectiveCompareAtPrice = compareAtPrice;

    // Get Loop subscription price data if available
    const loopProperties = cartItem.properties || {};
    const hasLoopSubscriptionData =
      cartItem.selling_plan_allocation ||
      loopProperties.subscription_id ||
      loopProperties.shipping_interval_frequency;

    // Determine subscription pricing from Loop data when available
    if (isSubscription && hasLoopSubscriptionData) {
      // Extract subscription price from available selling plan data
      if (cartItem.selling_plan_allocation?.price_adjustments?.length > 0) {
        // Modern Loop implementation using selling_plan_allocation
        const priceAdjustment = cartItem.selling_plan_allocation.price_adjustments[0];

        // Get price from selling plan allocation if available
        if (priceAdjustment && typeof priceAdjustment.price !== 'undefined') {
          finalPrice = parseFloat(priceAdjustment.price) / 100;
          effectiveCompareAtPrice = compareAtPrice || basePrice;
          shouldShowCompareAt = true;
        }
      }
      // If no price info in selling_plan, try to determine from variant pricing
      else if (effectiveCompareAtPrice) {
        // Keep the variant's compare at price
        shouldShowCompareAt = true;
      }
    }

    // Store pre-discount price for UI
    const preDiscountPrice = finalPrice;

    // Calculate discounted price if applicable
    if (discountPercent > 0) {
      // Only add compare-at price for one-time items if not already showing
      if (!shouldShowCompareAt && !isSubscription) {
        effectiveCompareAtPrice = finalPrice;
        shouldShowCompareAt = true;
      }

      // Apply discount to single item price
      const discountMultiplier = 1 - discountPercent / 100;
      finalPrice = finalPrice * discountMultiplier;
    }

    // Calculate base totals
    const totalBasePrice = (basePrice * quantity).toFixed(2);
    const totalPreDiscountPrice = (preDiscountPrice * quantity).toFixed(2);
    const totalCompareAtPrice =
      shouldShowCompareAt && effectiveCompareAtPrice
        ? (effectiveCompareAtPrice * quantity).toFixed(2)
        : null;

    // Calculate final price with Shopify's exact rounding pattern
    let totalFinalPrice;
    if (discountPercent > 0) {
      // Use the precise pattern observed in your data
      const mod4 = quantity % 4;
      const rawTotal = finalPrice * quantity;

      if (mod4 === 0 || mod4 === 1) {
        // Quantities divisible by 4 (4, 8) or remainder 1 (1, 5): Exact match
        totalFinalPrice = rawTotal.toFixed(2);
      } else if (mod4 === 2) {
        // Quantities with remainder 2 (2, 6): Shopify is 1¢ higher
        totalFinalPrice = (Math.ceil(rawTotal * 100) / 100).toFixed(2);
      } else if (mod4 === 3) {
        // Quantities with remainder 3 (3, 7): Shopify is 1¢ lower
        totalFinalPrice = (Math.floor(rawTotal * 100) / 100).toFixed(2);
      }
    } else {
      // No discount, standard calculation
      totalFinalPrice = (finalPrice * quantity).toFixed(2);
    }

    // Create structured result with all pricing information
    const result = {
      totalBasePrice,
      totalPreDiscountPrice,
      totalFinalPrice,
      totalCompareAtPrice,
      hasCompareAtPrice: shouldShowCompareAt,
      isSubscription,
      itemType,
      hasDiscount: discountPercent > 0,
      discountPercent,
      subscriptionData: hasLoopSubscriptionData
        ? {
            id: cartItem.subscription_id || loopProperties.subscription_id,
            frequency: loopProperties.shipping_interval_frequency,
            unit: loopProperties.shipping_interval_unit_type,
            sellingPlanId: cartItem.selling_plan_allocation?.selling_plan_id,
          }
        : null,
    };

    if (window.DEBUG) {
      console.log('Price calculation for', cartItem.id, {
        variant: variantTitle,
        itemType,
        isSubscription,
        hasLoopData: !!hasLoopSubscriptionData,
        originalPrice: basePrice,
        preDiscountPrice,
        finalPrice,
        compareAtPrice: effectiveCompareAtPrice,
        shouldShowCompareAt,
        discountPercent,
        quantity,
        mod4: quantity % 4,
        rawTotal: finalPrice * quantity,
        appliedPattern:
          quantity % 4 === 0
            ? 'div-by-4'
            : quantity % 4 === 1
            ? 'remainder-1'
            : quantity % 4 === 2
            ? 'remainder-2-add-1c'
            : 'remainder-3-sub-1c',
        totalFinalPrice,
        totalCompareAtPrice,
      });
    }

    return result;
  },

  // Proportional discount allocator function for multiple items
  allocateDiscountAcrossItems(cartItems, discountPercentage) {
    log('Allocating discount', discountPercentage, '% across', cartItems.length, 'items');

    // Get the exact Shopify subtotal from the DOM
    let shopifySubtotal = null;
    const subtotalElement = document.querySelector('.rebuy-cart__flyout-subtotal-final-amount');
    if (subtotalElement) {
      const subtotalText = subtotalElement.textContent.trim();
      const subtotalMatch = subtotalText.match(/[\d.,]+/);
      if (subtotalMatch) {
        const numericText = subtotalMatch[0].replace(/,/g, '');
        shopifySubtotal = parseFloat(numericText);
        log('Found Shopify subtotal in DOM:', shopifySubtotal);
      }
    }

    // Special case: Single item cart - use direct subtotal
    if (cartItems.length === 1) {
      const item = cartItems[0];
      const price = parseFloat(item.price) / 100;
      const itemTotal = price * item.quantity;

      // If we have the subtotal from Shopify, use it
      const discountedPrice =
        shopifySubtotal !== null ? shopifySubtotal : itemTotal * (1 - discountPercentage / 100);

      const discountAmount = itemTotal - discountedPrice;

      const allocations = new Map();
      allocations.set(item.id, {
        originalPrice: itemTotal,
        discountAmount: discountAmount,
        discountedPrice: discountedPrice,
      });

      return {
        allocations,
        totalDiscount: discountAmount,
        discountedTotal: discountedPrice,
      };
    }
    // For multiple items, use proportional allocation with DOM-based correction
    // Calculate original subtotal and expected discount
    const originalSubtotal = cartItems.reduce((sum, item) => {
      const price = parseFloat(item.price) / 100;
      return sum + price * item.quantity;
    }, 0);

    // Target total - use Shopify's if available, otherwise calculate
    const targetTotal =
      shopifySubtotal !== null
        ? shopifySubtotal
        : originalSubtotal * (1 - discountPercentage / 100);

    // First pass allocation - proportional but with floor rounding to avoid over-discount
    const allocations = new Map();
    let allocatedTotal = 0;

    cartItems.forEach(item => {
      const price = parseFloat(item.price) / 100;
      const itemTotal = price * item.quantity;
      const proportion = itemTotal / originalSubtotal;

      // Calculate discount for this item
      const itemDiscount = (originalSubtotal - targetTotal) * proportion;

      // Round to 2 decimal places using floor to avoid over-discounting
      const roundedDiscount = Math.floor(itemDiscount * 100) / 100;
      const discountedPrice = itemTotal - roundedDiscount;

      allocations.set(item.id, {
        originalPrice: itemTotal,
        discountAmount: roundedDiscount,
        discountedPrice: discountedPrice,
      });

      allocatedTotal += discountedPrice;
    });

    // Check if our total matches the target
    const roundedAllocatedTotal = Math.round(allocatedTotal * 100) / 100;
    const difference = targetTotal - roundedAllocatedTotal;

    // If there's a cent-level difference, adjust the largest item
    if (Math.abs(difference) >= 0.01) {
      let largestItemId = null;
      let largestPrice = 0;

      // Find the largest item by price
      cartItems.forEach(item => {
        const allocation = allocations.get(item.id);
        if (allocation && allocation.discountedPrice > largestPrice) {
          largestPrice = allocation.discountedPrice;
          largestItemId = item.id;
        }
      });

      if (largestItemId) {
        const allocation = allocations.get(largestItemId);
        allocation.discountedPrice += difference;
        allocation.discountAmount -= difference;
        allocations.set(largestItemId, allocation);
      }
    }

    // Calculate final totals
    let totalDiscount = 0;
    let discountedTotal = 0;

    for (const [_, allocation] of allocations.entries()) {
      totalDiscount += allocation.discountAmount;
      discountedTotal += allocation.discountedPrice;
    }

    return {
      allocations,
      totalDiscount,
      discountedTotal,
    };
  },

  // Get the HTML structure type of a price element
  getPriceStructureType: function (itemElement) {
    if (!itemElement) return 'unknown';

    const priceContainer = itemElement.querySelector('.rebuy-cart__flyout-item-price');
    if (!priceContainer) return 'unknown';

    // Check for subscription UI
    const hasSubscriptionDropdown = itemElement.querySelector('.rebuy-select') !== null;
    const hasSubscriptionButton = itemElement.querySelector('.rebuy-button.outline') !== null;
    const isSubscriptionItem = hasSubscriptionDropdown && !hasSubscriptionButton;

    // Check price structure in DOM
    const hasSaleElement = priceContainer.querySelector('.rebuy-money.sale') !== null;
    const hasCompareAtElement = priceContainer.querySelector('.rebuy-money.compare-at') !== null;
    const hasRegularPriceOnly =
      priceContainer.querySelector('.rebuy-money:not(.sale):not(.compare-at)') !== null;

    // Log detailed structure for debugging
    if (window.DEBUG) {
      console.log('Price structure detection:', {
        itemId: itemElement.getAttribute('data-item-id'),
        hasSaleElement,
        hasCompareAtElement,
        hasRegularPriceOnly,
        hasSubscriptionDropdown,
        hasSubscriptionButton,
        isSubscriptionItem,
        html: priceContainer.innerHTML,
      });
    }

    // Determine structure type
    if (hasSaleElement && hasCompareAtElement) {
      return isSubscriptionItem ? 'subscription-with-compare' : 'sale-with-compare';
    } else if (hasSaleElement) {
      return 'sale-only';
    } else if (hasRegularPriceOnly) {
      return isSubscriptionItem ? 'subscription-regular' : 'regular';
    } else {
      return 'unknown';
    }
  },

  // Update price text only without changing HTML structure
  updatePriceText: function (cartItem, priceInfo) {
    const { totalFinalPrice, totalCompareAtPrice, hasCompareAtPrice } = priceInfo;
    const itemElements = document.querySelectorAll(`[data-item-id="${cartItem.id}"]`);

    if (!itemElements || itemElements.length === 0) {
      if (window.DEBUG) console.error('Item element not found:', cartItem.id);
      return;
    }

    itemElements.forEach(itemElement => {
      const priceContainer = itemElement.querySelector('.rebuy-cart__flyout-item-price');
      if (!priceContainer) {
        if (window.DEBUG) console.error('Price container not found:', cartItem.id);
        return;
      }

      // Check for subscription UI to determine if this is a subscription
      const hasSubscriptionDropdown = itemElement.querySelector('.rebuy-select') !== null;
      const hasSubscriptionButton = itemElement.querySelector('.rebuy-button.outline') !== null;
      const isSubscriptionItem = hasSubscriptionDropdown && !hasSubscriptionButton;

      // Get current structure before we modify anything
      const currentStructure = window.RebuyUtils.getPriceStructureType(itemElement);

      if (window.DEBUG) {
        console.log('Updating price for item', cartItem.id, {
          currentStructure,
          isSubscriptionItem,
          totalFinalPrice,
          totalCompareAtPrice,
          hasCompareAtPrice,
        });
      }

      // Handle each structure type appropriately
      if (
        currentStructure === 'sale-with-compare' ||
        currentStructure === 'subscription-with-compare'
      ) {
        // Already has sale + compare-at structure, just update values
        const saleElement = priceContainer.querySelector('.rebuy-money.sale span:not(.sr-only)');
        const compareElement = priceContainer.querySelector(
          '.rebuy-money.compare-at span:not(.sr-only)'
        );

        if (saleElement) {
          saleElement.textContent = `$${totalFinalPrice}`;
        }

        if (compareElement && totalCompareAtPrice) {
          compareElement.textContent = `$${totalCompareAtPrice}`;
        }
      } else if (currentStructure === 'regular' || currentStructure === 'subscription-regular') {
        if (hasCompareAtPrice && totalCompareAtPrice) {
          // Need to convert from regular to sale+compare structure
          priceContainer.innerHTML = `
          <div>
            <span class="rebuy-money sale">
              <span class="sr-only">Sale price</span>
              <span>$${totalFinalPrice}</span>
            </span>
            <span class="rebuy-money compare-at">
              <span class="sr-only">Original price</span>
              <span>$${totalCompareAtPrice}</span>
            </span>
          </div>
        `;
        } else {
          // Keep as regular price, just update the value
          const priceElement = priceContainer.querySelector('.rebuy-money span:not(.sr-only)');
          if (priceElement) {
            priceElement.textContent = `$${totalFinalPrice}`;
          }
        }
      } else if (currentStructure === 'unknown') {
        // Fallback for unknown structure - replace with appropriate structure
        if (hasCompareAtPrice && totalCompareAtPrice) {
          priceContainer.innerHTML = `
          <div>
            <span class="rebuy-money sale">
              <span class="sr-only">Sale price</span>
              <span>$${totalFinalPrice}</span>
            </span>
            <span class="rebuy-money compare-at">
              <span class="sr-only">Original price</span>
              <span>$${totalCompareAtPrice}</span>
            </span>
          </div>
        `;
        } else {
          priceContainer.innerHTML = `
          <div>
            <span class="rebuy-money">
              <span class="sr-only">Price</span>
              <span>$${totalFinalPrice}</span>
            </span>
          </div>
        `;
        }
      }
    });
  },

  // Add this utility function for direct DOM updates
  updateDOMPrices: function (cartItems, allocations) {
    cartItems.forEach(item => {
      const itemId = item.id;
      if (!allocations.has(itemId)) return;

      const allocation = allocations.get(itemId);
      const discountedPrice = allocation.discountedPrice.toFixed(2);
      const originalPrice = allocation.originalPrice.toFixed(2);

      // Find all elements for this item (could be multiple)
      const itemElements = document.querySelectorAll(`[data-item-id="${itemId}"]`);

      itemElements.forEach(itemElement => {
        const priceContainer = itemElement.querySelector('.rebuy-cart__flyout-item-price');
        if (!priceContainer) return;

        // Update the prices based on current structure
        // Look for sale price structure first
        const hasCompareAtStructure =
          priceContainer.querySelector('.rebuy-money.compare-at') !== null;

        if (hasCompareAtStructure) {
          // Already has a sale structure, just update the values
          const saleElement = priceContainer.querySelector('.rebuy-money.sale span:not(.sr-only)');
          const compareElement = priceContainer.querySelector(
            '.rebuy-money.compare-at span:not(.sr-only)'
          );

          if (saleElement) saleElement.textContent = `$${discountedPrice}`;
          if (compareElement) compareElement.textContent = `$${originalPrice}`;
        } else {
          // Regular price structure - convert to sale structure
          priceContainer.innerHTML = `
          <div>
            <span class="rebuy-money sale">
              <span class="sr-only">Sale price</span>
              <span>$${discountedPrice}</span>
            </span>
            <span class="rebuy-money compare-at">
              <span class="sr-only">Original price</span>
              <span>$${originalPrice}</span>
            </span>
          </div>
        `;
        }
      });
    });
  },

  // Enhanced detection of item type
  isSubscriptionItem: function (itemElement) {
    if (!itemElement) return false;

    // Check for switching attribution in class
    const hasSubscriptionSwitch = itemElement.classList.contains(
      'property-_attribution:rebuy-switch-to-subscription'
    );
    const hasOnetimeSwitch = itemElement.classList.contains(
      'property-_attribution:rebuy-switch-to-onetime'
    );

    // Check for UI elements
    const hasSubscriptionDropdown = itemElement.querySelector('.rebuy-select') !== null;
    const hasSubscriptionButton = itemElement.querySelector('.rebuy-button.outline') !== null;

    // Determine item type
    if (hasSubscriptionSwitch) {
      return 'switched-to-subscription';
    } else if (hasOnetimeSwitch) {
      return 'switched-to-onetime';
    } else if (hasSubscriptionDropdown && !hasSubscriptionButton) {
      return 'pure-subscription';
    } else if (hasSubscriptionButton) {
      return 'pure-onetime';
    }

    // Fallback
    return 'unknown';
  },
};
